
import { createPenaltyBlock, deactivatePenaltyBlock, listPenaltyBlocks, updatePenaltyBlock } from "@/services/api/penaltyBlocksApi";
import { defineComponent } from 'vue';
import { PenaltyBlock } from 'shared/PenaltyBlock';
import BaseModal from '@/components/ui/BaseModal.vue';
import StateBlock from '@/components/ui/StateBlock.vue';

interface BlockForm {
  code: string;
  name: { en: string; es: string };
  description: { en: string; es: string };
  category: string;
  severity: number;
  isActive: boolean;
}

export default defineComponent({
  name: 'PenaltyBlockManagerComponent',
  components: { BaseModal, StateBlock },
  data() {
    return {
      blocks: [] as PenaltyBlock[],
      filteredBlocks: [] as PenaltyBlock[],
      loading: false,
      saving: false,
      isEditing: false,
      editingBlockId: null as string | null,
      
      // Filters
      searchQuery: '',
      categoryFilter: '',
      statusFilter: '',
      sortBy: 'code',
      
      // Form
      blockForm: {
        code: '',
        name: { en: '', es: '' },
        description: { en: '', es: '' },
        category: '',
        severity: 5,
        isActive: true
      } as BlockForm,
      
      errors: {
        code: '',
        nameEn: '',
        severity: ''
      },
      
      // Modals
      showBlock: false,
      blockToDeactivate: null as PenaltyBlock | null,
      
      // Toast
      showToast: false,
      toastTitle: '',
      toastMessage: '',
      toastIcon: 'fas fa-info-circle text-info'
    };
  },
  computed: {
    categories(): string[] {
      const cats = new Set<string>();
      this.blocks.forEach(block => {
        if (block.category) {
          cats.add(block.category);
        }
      });
      return Array.from(cats).sort();
    }
  },
  async mounted() {
    await this.loadBlocks();
  },
  methods: {
    async loadBlocks() {
      this.loading = true;
      try {
        const response = await listPenaltyBlocks();
        this.blocks = response as unknown as PenaltyBlock[];
        this.applyFilters();
      } catch (error) {
        console.error('Failed to load penalty blocks:', error);
        this.showNotification(this.$t('panel.penaltyBlocks.errorTitle'), this.$t('panel.penaltyBlocks.loadFailed'), 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async refreshBlocks() {
      await this.loadBlocks();
    },
    
    applyFilters() {
      let filtered = [...this.blocks];
      
      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(block => 
          block.code.toLowerCase().includes(query) ||
          block.name.en.toLowerCase().includes(query) ||
          block.name.es?.toLowerCase().includes(query)
        );
      }
      
      // Category filter
      if (this.categoryFilter) {
        filtered = filtered.filter(block => block.category === this.categoryFilter);
      }
      
      // Status filter
      if (this.statusFilter) {
        const isActive = this.statusFilter === 'active';
        filtered = filtered.filter(block => block.isActive === isActive);
      }
      
      // Sort
      filtered.sort((a, b) => {
        switch (this.sortBy) {
          case 'name':
            return a.name.en.localeCompare(b.name.en);
          case 'severity':
            return (b.severity || 0) - (a.severity || 0);
          case 'category':
            return (a.category || '').localeCompare(b.category || '');
          default:
            return a.code.localeCompare(b.code);
        }
      });
      
      this.filteredBlocks = filtered;
    },
    
    formatCode() {
      this.blockForm.code = this.blockForm.code
        .toUpperCase()
        .replace(/\s+/g, '_')
        .replace(/[^A-Z0-9_]/g, '')
        .substring(0, 20);
    },
    
    formatCategory(category: string): string {
      const key = `penalty.categories.${category.toLowerCase()}`;
      if (this.$te(key)) return this.$t(key) as string;
      return category.charAt(0).toUpperCase() + category.slice(1);
    },

    blockName(block: PenaltyBlock): string {
      const es = this.$i18n.locale === 'es';
      return (es ? block.name.es || block.name.en : block.name.en || block.name.es) || block.code;
    },

    blockAltName(block: PenaltyBlock): string {
      const es = this.$i18n.locale === 'es';
      const alt = es ? block.name.en : block.name.es;
      return alt && alt !== this.blockName(block) ? alt : '';
    },
    
    getCategoryBadgeClass(category: string): string {
      const classes: Record<string, string> = {
        warning: 'bg-warning text-dark',
        ban: 'bg-danger',
        fine: 'bg-info',
        restriction: 'bg-secondary',
        penalty: 'bg-primary',
        item: 'bg-success',
        other: 'bg-dark border'
      };
      return classes[category] || 'bg-secondary';
    },
    
    getCategoryIcon(category: string): string {
      const icons: Record<string, string> = {
        warning: 'fas fa-exclamation-triangle',
        ban: 'fas fa-ban',
        fine: 'fas fa-dollar-sign',
        restriction: 'fas fa-lock',
        penalty: 'fas fa-gavel',
        item: 'fas fa-box',
        other: 'fas fa-tag'
      };
      return icons[category] || 'fas fa-tag';
    },
    
    getSeverityBadgeClass(severity: number): string {
      if (severity <= 3) return 'bg-success';
      if (severity <= 6) return 'bg-warning text-dark';
      if (severity <= 9) return 'bg-orange';
      return 'bg-danger';
    },
    
    resetForm() {
      this.blockForm = {
        code: '',
        name: { en: '', es: '' },
        description: { en: '', es: '' },
        category: '',
        severity: 5,
        isActive: true
      };
      this.errors = { code: '', nameEn: '', severity: '' };
      this.isEditing = false;
      this.editingBlockId = null;
    },
    
    openCreateModal() {
      this.resetForm();
      this.showModal();
    },
    
    openEditModal(block: PenaltyBlock) {
      this.isEditing = true;
      this.editingBlockId = block._id;
      this.blockForm = {
        code: block.code,
        name: { 
          en: block.name.en || '', 
          es: block.name.es || '' 
        },
        description: { 
          en: block.description?.en || '', 
          es: block.description?.es || '' 
        },
        category: block.category || '',
        severity: block.severity || 5,
        isActive: block.isActive
      };
      this.showModal();
    },
    
    showModal() {
      this.showBlock = true;
    },
    
    hideModal() {
      this.showBlock = false;
    },
    
    validateForm(): boolean {
      this.errors = { code: '', nameEn: '', severity: '' };
      let isValid = true;
      
      if (!this.blockForm.code) {
        this.errors.code = this.$t('panel.penaltyBlocks.codeRequired');
        isValid = false;
      } else if (this.blockForm.code.length > 20) {
        this.errors.code = this.$t('panel.penaltyBlocks.codeTooLong');
        isValid = false;
      }
      
      if (!this.blockForm.name.en) {
        this.errors.nameEn = this.$t('panel.penaltyBlocks.nameEnRequired');
        isValid = false;
      }
      
      if (this.blockForm.severity < 1 || this.blockForm.severity > 10) {
        this.errors.severity = this.$t('panel.penaltyBlocks.severityRange');
        isValid = false;
      }
      
      return isValid;
    },
    
    async saveBlock() {
      if (!this.validateForm()) {
        return;
      }
      
      this.saving = true;
      try {
        const payload = {
          code: this.blockForm.code,
          name: this.blockForm.name,
          description: this.blockForm.description.en || this.blockForm.description.es 
            ? this.blockForm.description 
            : undefined,
          category: this.blockForm.category || undefined,
          severity: this.blockForm.severity,
          isActive: this.blockForm.isActive
        };
        
        // 4xx resolves (validateStatus < 500) with { error }: show it, never the success toast
        const result: any = this.isEditing && this.editingBlockId
          ? await updatePenaltyBlock(this.editingBlockId, payload)
          : await createPenaltyBlock(payload);
        if (result && result.error) {
          this.showNotification(this.$t('panel.penaltyBlocks.errorTitle'), String(result.error), 'error');
          return;
        }
        this.showNotification(
          this.$t('panel.penaltyBlocks.successTitle'),
          this.isEditing ? this.$t('panel.penaltyBlocks.updated') : this.$t('panel.penaltyBlocks.created'),
          'success'
        );
        
        this.hideModal();
        await this.loadBlocks();
      } catch (error: any) {
        const message = error.response?.data?.error || this.$t('panel.penaltyBlocks.saveFailed');
        this.showNotification(this.$t('panel.penaltyBlocks.errorTitle'), message, 'error');
      } finally {
        this.saving = false;
      }
    },
    
    confirmDeactivate(block: PenaltyBlock) {
      this.blockToDeactivate = block;
    },
    
    hideDeactivateModal() {
      this.blockToDeactivate = null;
    },
    
    async deactivateBlock() {
      if (!this.blockToDeactivate) return;
      
      this.saving = true;
      try {
        const result: any = await deactivatePenaltyBlock(this.blockToDeactivate._id);
        if (result && result.error) {
          this.showNotification(this.$t('panel.penaltyBlocks.errorTitle'), String(result.error), 'error');
          return;
        }
        this.showNotification(this.$t('panel.penaltyBlocks.successTitle'), this.$t('panel.penaltyBlocks.deactivated'), 'success');
        this.hideDeactivateModal();
        await this.loadBlocks();
      } catch (error: any) {
        const message = error.response?.data?.error || this.$t('panel.penaltyBlocks.deactivateFailed');
        this.showNotification(this.$t('panel.penaltyBlocks.errorTitle'), message, 'error');
      } finally {
        this.saving = false;
      }
    },
    
    async activateBlock(block: PenaltyBlock) {
      this.saving = true;
      try {
        const result: any = await updatePenaltyBlock(block._id, {
          ...block,
          isActive: true
        });
        if (result && result.error) {
          this.showNotification(this.$t('panel.penaltyBlocks.errorTitle'), String(result.error), 'error');
          return;
        }
        this.showNotification(this.$t('panel.penaltyBlocks.successTitle'), this.$t('panel.penaltyBlocks.activated'), 'success');
        await this.loadBlocks();
      } catch (error: any) {
        const message = error.response?.data?.error || this.$t('panel.penaltyBlocks.activateFailed');
        this.showNotification(this.$t('panel.penaltyBlocks.errorTitle'), message, 'error');
      } finally {
        this.saving = false;
      }
    },
    
    showNotification(title: string, message: string, type: 'success' | 'error' | 'info') {
      this.toastTitle = title;
      this.toastMessage = message;
      switch (type) {
        case 'success':
          this.toastIcon = 'fas fa-check-circle text-success';
          break;
        case 'error':
          this.toastIcon = 'fas fa-times-circle text-danger';
          break;
        default:
          this.toastIcon = 'fas fa-info-circle text-info';
      }
      this.showToast = true;
      setTimeout(() => {
        this.showToast = false;
      }, 4000);
    }
  }
});
